// @flow

import _ from 'lodash';
import memoize from 'memoize-weak';

import { Colors } from '@zapier/theme';
import { compareNatural } from '@zapier/services/src/utils';
import {
  getVersion,
  hasVersion,
  stripVersion,
} from '@zapier/common-utils/src/services';

import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import fuzzyscore from 'app/common/fuzzyscore';
import fuzzysearch from 'app/common/fuzzysearch';
import { ZT_LIVE_DATA_SERVICES } from 'app/beta-editor/constants';
import { isPathApi } from 'app/beta-editor/utils/PathUtils';
import { NAME, nodeTypes } from 'app/service/constants';
import {
  actionHelpText,
  actionHelpTextHTML,
  compareVersions,
  exists,
  get,
  getActionMeta,
  getActionName,
} from 'app/common/CommonUtils';

import type { EditorAuthsMap } from 'app/beta-editor/types';
import type { Flow, FlowNode, FlowNodeType } from 'app/beta-editor/flowState';
import type {
  SelectListItem,
  Service,
  ServiceAction,
  ServiceField,
  ServicesMap,
} from 'app/service/types';

export * from '@zapier/services/src/utils/sortByPopularityRank'; // TODO: unnecessary?

const actionTypes = [
  nodeTypes.HAS_SEARCHES,
  nodeTypes.HAS_WRITES,
  nodeTypes.HAS_FILTERS,
];

const typeKeys = {
  read: [nodeTypes.HAS_READS],
  search: actionTypes,
  search_or_write: actionTypes,
  write: actionTypes,
  filter: actionTypes,
  paths_group: actionTypes,
};

const isPathTrue = (path, service) => Boolean(_.get(service, path, null));
const isAnyPathTrue = (paths, service) =>
  paths.some(path => isPathTrue(path, service));

// The multiple keys here represent legacy data structure and the updated
// GraphQL equivalent
export const isBeta = _.partial(isAnyPathTrue, ['beta', 'isBeta']);
export const isBuiltIn = _.partial(isAnyPathTrue, ['builtIn', 'isBuiltIn']);
export const isDeprecated = _.partial(isAnyPathTrue, [
  'deprecated',
  'isDeprecated',
]);
export const isFeatured = _.partial(isAnyPathTrue, ['featured', 'isFeatured']);
export const isHidden = _.partial(isAnyPathTrue, ['hidden', 'isHidden']);
export const isPremium = _.partial(isAnyPathTrue, ['premium', 'isPremium']);
export const isInvite = _.partial(isAnyPathTrue, ['invite_app', 'isInvite']);
export const isPublic = _.partial(isAnyPathTrue, ['public', 'isPublic']);
export const isOauth = (service: Service) => {
  const authType = _.get(service, 'authType', null);
  const oauthService = _.get(service, ['meta', 'oauth'], false);
  return authType === 'oauth' || oauthService;
};
export const hasUpfrontFields = (service: Service) => {
  return (
    Boolean(service.hasUpfrontFields) || !_.isEmpty(service.upfront_fields)
  );
};
export const excludePathsApi = (services: Service[]) =>
  _.filter(services, ({ key }) => !isPathApi(key));

type SelectListItemsOptions = { isRestrictedChoice: boolean };
const makeSelectListItems = (
  node,
  actions: ?(ServiceAction[]),
  itemType,
  { isRestrictedChoice }: SelectListItemsOptions = { isRestrictedChoice: false }
) => {
  const allUnimportant = _.every(
    actions,
    action => (action.important || false) === false
  );

  return _.chain(actions)
    .filter(action => {
      // Ensures that actions that are { `hide`: true } are displayed if the node is set to that action
      if (node.action === action.key) {
        if (
          node.type_of === itemType ||
          (node.type_of === 'search_or_write' && itemType === 'search')
        ) {
          return true;
        }
      }
      if (isRestrictedChoice) {
        return false;
      }
      return !action.hide;
    })
    .map(action => {
      const helpText = actionHelpText(action);
      const helpTextHTML = actionHelpTextHTML(action);
      return {
        important: allUnimportant ? true : action.important || false,
        label: action.label,
        helpText,
        helpTextHTML,
        itemKey: action.key,
        itemType,
      };
    })
    .value();
};

export const transformSelectListItems = (
  node: FlowNode,
  service: Service,
  opts: SelectListItemsOptions
) => {
  const items = [];

  if (node.type_of === 'read') {
    items.push({
      itemType: 'read',
      header: '',
      items: makeSelectListItems(node, service.read, 'read', opts),
    });
  } else {
    const writeItems = makeSelectListItems(node, service.write, 'write', opts);

    if (writeItems.length) {
      items.push({
        itemType: 'write',
        header: 'Create',
        items: writeItems,
      });
    }

    const searchItems = makeSelectListItems(
      node,
      service.search,
      'search',
      opts
    );

    if (searchItems.length) {
      items.push({
        itemType: 'search',
        header: 'Search',
        items: searchItems,
      });
    }

    const filterItems = makeSelectListItems(
      node,
      service.filter,
      'filter',
      opts
    );

    if (filterItems.length) {
      items.push({
        itemType: 'filter',
        header: 'Filter',
        items: filterItems,
      });
    }
  }

  return items;
};

const nodeTypeToDelegateMap: { [FlowNodeType]: string } = {
  [nodeTypes.SEARCH_OR_WRITE]: 'key',
  [nodeTypes.WRITE]: 'write_delegate',
  [nodeTypes.SEARCH]: 'search_delegate',
};

const getSearchOrWriteMeta = (service, currentAction, currentTypeOf) => {
  const lookupKey = nodeTypeToDelegateMap[currentTypeOf];

  return _.find(service.search_or_write, { [lookupKey]: currentAction });
};

export type ServiceRecommendation = {
  slug: string,
  score: number,
};
const getRecommendationScore = (
  service: Service,
  recommendationScoresBySlug: { [slug: string]: ?ServiceRecommendation }
) => {
  const slug = _.get(service, 'slug');
  if (!slug) {
    return 0;
  }

  return _.get(recommendationScoresBySlug, [slug, 'score'], 0);
};

export const sortByRecommendations = memoize(
  (
    services: Array<Service>,
    recommendationScores: Array<ServiceRecommendation>
  ) => {
    const recommendationScoresBySlug =
      _.keyBy(recommendationScores, 'slug') || {};

    return services.sort((serviceA, serviceB) => {
      const scoreServiceA = getRecommendationScore(
        serviceA,
        recommendationScoresBySlug
      );
      const scoreServiceB = getRecommendationScore(
        serviceB,
        recommendationScoresBySlug
      );

      const compareScore = scoreServiceB - scoreServiceA;
      if (compareScore) {
        return compareScore;
      }

      return compareNatural(
        _.toString(serviceA.name),
        _.toString(serviceB.name)
      );
    });
  }
);

const isServiceActionVisible = (action: ServiceAction) => !action.hide;

export const getServiceActionsAvailableToNode = (
  node: FlowNode,
  service: Service
) => {
  if (!node || !service) {
    return [];
  }
  if (node.type_of === 'read') {
    return service[node.type_of] || [];
  }
  const writes = service.write || [];
  const searches = service.search || [];
  const filters = service.filter || [];
  return [...writes, ...searches, ...filters];
};

export const getAutoSelectedAction = (
  node: ?FlowNode,
  service: Service
): ?SelectListItem => {
  const serviceActions = node
    ? getServiceActionsAvailableToNode(node, service)
    : [];
  const visibleServiceActions = serviceActions.filter(isServiceActionVisible);
  if (node && serviceActions && visibleServiceActions.length === 1) {
    const selectItems = transformSelectListItems(node, service, {
      isRestrictedChoice: false,
    });
    const targetAction = visibleServiceActions[0];
    const selectItemAction = _.find(selectItems, selectItem => {
      const { items } = selectItem;
      const matchingSelectItem = items.filter(
        ({ itemKey }) => itemKey === targetAction.key
      )[0];
      return Boolean(matchingSelectItem);
    });
    return selectItemAction
      ? {
          ..._.omit(selectItemAction, 'items'),
          ...selectItemAction.items[0],
        }
      : null;
  }
  return null;
};

const ServiceUtils = {
  name(service: Service = {}) {
    if (service.name) {
      return service.name;
    }

    if (service.title) {
      return service.title;
    }

    if (exists(service.meta, ['human_readable_key'])) {
      return get(service.meta, ['human_readable_key']);
    }

    return service.key || 'Unnamed service';
  },

  nameFromApi(api: string, services: ServicesMap | {}) {
    return ServiceUtils.name(services[api]);
  },

  requiresLiveData(service: Service) {
    // For now, we use app source in ZT only for Formatter and Code
    return _.includes(ZT_LIVE_DATA_SERVICES, service.key);
  },

  needsAuth(service: Service) {
    const upfrontFields = _.get(service, 'upfront_fields', []);
    const filteredUpfrontFields = ServiceUtils.rawUpfronts(upfrontFields);

    // `service.authType` is provided by the GraphQL response, if it does not
    // return `null`, the service requires some sort of auth
    if (service.authType) {
      return true;
    }

    if (filteredUpfrontFields.length > 0) {
      return true;
    }

    if (_.get(service, ['meta', 'oauth'], false)) {
      return true;
    }

    return false;
  },

  rawUpfronts(upfrontFields: Array<ServiceField>): Array<ServiceField> {
    if (!upfrontFields) {
      return [];
    }

    return upfrontFields.filter(field => {
      return !(exists(field.important) && field.important === false);
    });
  },

  actionName(
    service: Service,
    actionKey: string,
    typeOf: FlowNodeType = nodeTypes.READ,
    options: { hasNoVerb: boolean }
  ) {
    const action = ServiceUtils.actionMeta(service, actionKey, typeOf);
    return getActionName(action, options);
  },

  actionNoun(
    service: Service,
    actionKey: string,
    typeOf: FlowNodeType = nodeTypes.READ
  ) {
    if (typeOf === nodeTypes.SEARCH_OR_WRITE) {
      // Meta for a search_or_write doesn't contain the nouns of either, so we default to use search
      typeOf = nodeTypes.SEARCH;
    }

    const noun = ServiceUtils.actionName(service, actionKey, typeOf, {
      hasNoVerb: true,
    });
    return (noun || 'Item').toString();
  },

  allVisibleLabelsOfType(
    type: 'reads' | 'searches' | 'writes',
    service: Service
  ) {
    return _.chain(service)
      .get(['actions', type])
      .filter(action => !_.get(action, 'hide'))
      .map(action => _.get(action, 'label'))
      .filter(Boolean)
      .uniq()
      .sortBy(noun => (noun || '').toLowerCase())
      .value();
  },

  allVisibleReadLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('reads', service);
  },

  allVisibleWriteLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('writes', service);
  },

  allVisibleSearchLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('searches', service);
  },

  actionMeta(
    service: Service,
    actionKey: string,
    typeOf: FlowNodeType = nodeTypes.READ
  ) {
    if (!service || !service[typeOf]) {
      return null;
    }
    return getActionMeta(service[typeOf], actionKey);
  },

  connectedServices(
    services: ServicesMap | Service[],
    auths: EditorAuthsMap,
    flow: Flow
  ): Service[] {
    const authApis = _.fromPairs(
      _.values(auths).map(auth => [auth.selected_api, true])
    );
    const flowApis = _.fromPairs(
      _.values(flow.nodes).map(node => [node.selected_api, true])
    );
    const apis = _.extend(flowApis, authApis);
    const servicesArray = Array.isArray(services)
      ? services
      : _.values(services);
    return servicesArray.filter(service => apis[service.key]);
  },

  banner(service: { banner: string } | Service) {
    return _.get(service, 'banner', null);
  },

  /*
   * Finds the action associated with the passed in service and current action.
   * Useful for e.g. finding the 'search' action corresponding to a 'search_or_write' action, or vice-versa.
   */
  getAssociatedAction({
    service,
    currentAction,
    currentTypeOf,
    desiredTypeOf,
  }: {
    service: Service,
    currentAction: string,
    currentTypeOf: FlowNodeType,
    desiredTypeOf: FlowNodeType,
  }) {
    const searchOrWriteMeta = getSearchOrWriteMeta(
      service,
      currentAction,
      currentTypeOf
    );
    if (!searchOrWriteMeta) {
      return null;
    }

    const key = nodeTypeToDelegateMap[desiredTypeOf];
    if (!key) {
      return null;
    }

    return searchOrWriteMeta[key];
  },

  matchesQuery: (query: string, service: Object) => {
    return fuzzysearch(query, service[NAME]);
  },

  queryScore: (query: string, service: Object) => {
    return -fuzzyscore(query, service[NAME]);
  },

  search: memoize((query: string, services: Array<Service>) => {
    if (!query) {
      return services;
    }

    return services
      .filter(_.partial(ServiceUtils.matchesQuery, query))
      .sort((serviceA, serviceB) => {
        const compareQueryScore =
          ServiceUtils.queryScore(query, serviceA) -
          ServiceUtils.queryScore(query, serviceB);
        if (compareQueryScore) {
          return compareQueryScore;
        }

        return 0;
      });
  }),

  getCommonProblemsHelpLink: (service = {}) => {
    return service.slug
      ? `/apps/${service.slug}/help#common-problems-${service.slug}`
      : '/help';
  },

  getVisibleServices(services: ServicesMap | Array<Service>): Array<Service> {
    return _.filter(services, service => !isHidden(service));
  },

  getAvailableServices(
    services: ServicesMap | Array<Service>,
    {
      nodeType,
      onlyPublic = false,
    }: {
      nodeType: FlowNodeType,
      onlyPublic: boolean,
    }
  ) {
    const maybePublicCheck: (service: Service) => boolean = onlyPublic
      ? isPublic
      : _.stubTrue;

    const nodeTypeKeys = typeKeys[nodeType] || ['unknown'];
    return _.filter(
      services,
      service =>
        !service.hidden &&
        _.some(nodeTypeKeys, k => service[k]) &&
        maybePublicCheck(service)
    );
  },

  getServicesAvailableToNode({
    nodeSelectedApi,
    services,
    onlyPublic = false,
    showPathApi = true,
  }: {
    nodeSelectedApi?: string,
    services: ServicesMap | Service[],
    onlyPublic?: boolean,
    showPathApi: boolean,
  }) {
    const maybePublicCheck: (service: Service) => boolean = onlyPublic
      ? isPublic
      : _.stubTrue;
    // Hide deprecated services except for Nodes already using them.
    return _.filter(services, service => {
      return (
        _.get(service, 'id') === nodeSelectedApi ||
        (!isDeprecated(service) &&
          maybePublicCheck(service) &&
          (showPathApi || !isPathApi(service.key)))
      );
    });
  },

  getYourServices(
    selectedApi: string,
    auths: EditorAuthsMap,
    flow: Flow,
    services: Service[],
    transform: (Service[]) => Service[]
  ): Service[] {
    const servicesArray = ServiceUtils.connectedServices(services, auths, flow);

    // move the current node's selected_api to first position for highlighting current
    const fromIndex = _.indexOf(
      _.map(servicesArray, service => service.key),
      selectedApi
    );
    if (fromIndex !== -1) {
      servicesArray.splice(0, 0, servicesArray.splice(fromIndex, 1)[0]);
    }

    return transform(servicesArray);
  },

  getServicesByKey(partialServices: Service[], services: Service[]): Service[] {
    const partialServicesIds = partialServices.map(service => service.key);
    return services.filter(service => partialServicesIds.includes(service.key));
  },

  getFeaturedServices(services: Service[], transform = s => s) {
    return transform(_.filter(services, ServiceUtils.isFeatured));
  },

  getRecommendedServices(services: Service[], transform = s => s, max = 8) {
    return _.take(transform(services), max);
  },

  getBuiltInServices(services: Service[], transform = s => s) {
    return transform(_.filter(services, ServiceUtils.isBuiltIn));
  },

  getAuthServices(services: ServicesMap): Service[] {
    return _.filter(
      services,
      service => !isHidden(service) && ServiceUtils.needsAuth(service)
    );
  },

  // This function will filter out any Service that does not contain an `id`.
  // According to the flow type for Service, it is possible to have an `id` that
  // is `null` or `undefined`.
  // https://github.com/zapier/zapier/blob/5540c1adc7c0de3f23456d0a3b656311b6d490cd/web/frontend/assets/app/service/types.js#L35
  getServicesById(services: Array<Service>): ServicesMap {
    return _.reduce(
      services,
      (acc, service) => {
        if (service && service.id) {
          acc[service.id] = service;
        }

        return acc;
      },
      {}
    );
  },

  getServiceByKey(services: ServicesMap | Service[], serviceKey: ?string) {
    return _.find(services, ({ key }) => key === serviceKey);
  },

  getServiceByKeyWithOptionalVersion(
    services: ServicesMap | Service[],
    serviceKey: string
  ) {
    const withAnyVersion = `${stripVersion(serviceKey)}@`;
    const possibleServices = _.filter(
      services,
      ({ key }) =>
        key === serviceKey || (key || '').indexOf(withAnyVersion) === 0
    );
    const selectedService = possibleServices
      .sort((a, b) => {
        if (a.key === serviceKey) {
          return 1;
        }
        if (b.key === serviceKey) {
          return -1;
        }
        return compareVersions(getVersion(a.key), getVersion(b.key));
      })
      .reverse()[0];
    return selectedService;
  },

  getServiceBySlug(services: ServicesMap | Service[], serviceSlug: string) {
    return _.find(services, ({ slug }) => slug === serviceSlug);
  },

  cleanServiceClassname(key: string, size: string) {
    if (_.isEmpty(key)) {
      return key;
    }

    const className = hasVersion(key) ? stripVersion(key) : key;

    return className.includes(size) ? className : `${className}${size}`;
  },

  getServiceKeyWithoutVersion(service: Service = {}) {
    return stripVersion(service.key);
  },

  getServiceName(service: Service) {
    return _.trim((service[NAME] || '').replace('by Zapier', ''));
  },

  buildSubtitleMessage(
    service: Service,
    {
      isDisabled,
      nodeType,
    }: {
      isDisabled: boolean,
      nodeType: FlowNodeType,
    }
  ) {
    if (!isDisabled) {
      return '';
    }
    if (isPathApi(service.key)) {
      return 'Paths Group Only Available On Last Step of Zap or Path';
    }
    if (ServiceUtils.isTrigger(nodeType)) {
      return 'No Trigger Available (Action only)';
    }
    return 'No Action Available (Trigger only)';
  },

  hasNoTriggersOrActions(service: Service): boolean {
    const types = [
      nodeTypes.HAS_READS,
      nodeTypes.HAS_WRITES,
      nodeTypes.HAS_SEARCHES,
      nodeTypes.HAS_FILTERS,
      nodeTypes.HAS_SEARCH_OR_WRITE,
    ];

    return types.every(type => !service[type]);
  },

  isTrigger(nodeType: FlowNodeType) {
    return nodeType === nodeTypes.READ;
  },

  hasVisibleTriggers(service: Service) {
    return service[nodeTypes.HAS_READS] === true;
  },

  hasVisibleActions(service: Service): boolean {
    const types = [
      nodeTypes.HAS_WRITES,
      nodeTypes.HAS_SEARCHES,
      nodeTypes.HAS_FILTERS,
      nodeTypes.HAS_SEARCH_OR_WRITE,
    ];

    return types.some(type => service[type] === true);
  },

  isServiceDisabled(
    service: Service,
    { nodeType, isLastStep }: { nodeType: FlowNodeType, isLastStep: boolean }
  ) {
    service = service || {};

    if (!isLastStep && isPathApi(service.key)) {
      return true;
    }

    // check for half-built public dev apps with no triggers/actions/searches/searchOrWrites:
    if (ServiceUtils.hasNoTriggersOrActions(service)) {
      return true;
    }

    if (ServiceUtils.isTrigger(nodeType)) {
      return !ServiceUtils.hasVisibleTriggers(service);
    }

    return !ServiceUtils.hasVisibleActions(service);
  },

  getPrimaryColor(service: Service) {
    if (isBuiltIn(service)) {
      return Colors.lava;
    }

    const primaryColor = _.get(service, ['primaryColor']);
    return primaryColor ? `#${primaryColor}` : Colors.heroine;
  },

  getHeadAndTailServiceCTASteps({
    flow,
    services,
  }: {
    flow: Flow,
    services: ServicesMap,
  }) {
    const [headServiceNodeId, tailServiceNodeId] = flow.nodeIds;

    const headServiceNode = flow.nodes[headServiceNodeId];
    const headNodeType = NodeUtils.getNodeType(headServiceNode);
    const headNodeAction = headServiceNode.action;
    const headService = NodeUtils.service(headServiceNode, services);

    const tailServiceNode = flow.nodes[tailServiceNodeId];
    const tailNodeType = NodeUtils.getNodeType(tailServiceNode);
    const tailNodeAction = tailServiceNode.action;
    const tailService = NodeUtils.service(tailServiceNode, services);

    const headServiceActionNoun = headService
      ? ServiceUtils.actionNoun(
          headService,
          headNodeAction,
          // This will never be 'paths_group' (or at least it can't be right now)
          // but I'm not sure how to make both types work here.
          // This function is only used so far for simple zap setup
          // which uses a strict set of criteria that need to be satisfied to use the zap template
          // as a 'sizzle' zap. Currently Paths will not be in any of these sizzle ZTs.
          // Flow knows that it's possible for headNodeType or tailNodeType here to be
          // PATHS_GROUP because getNodeType can return PATHS_GROUP.
          // But in reality humans know that it won't ever be PATHS_GROUP because none of the
          // zap templates we've hand picked use paths.
          // $FlowFixMe
          headNodeType
        ).toLowerCase()
      : '';

    const tailServiceActionNoun = tailService
      ? ServiceUtils.actionNoun(
          tailService,
          tailNodeAction,
          // This will never be 'paths_group' (or at least it can't be right now)
          // see comment above
          // $FlowFixMe
          tailNodeType
        ).toLowerCase()
      : '';

    return [
      {
        text: `1. Creating a new ${headServiceActionNoun} in`,
        serviceId: headService ? headService.id : '',
        serviceName: headService ? headService.name : '',
      },
      {
        text: `2. Look for a new ${tailServiceActionNoun} in`,
        serviceId: tailService ? tailService.id : '',
        serviceName: tailService ? tailService.name : '',
      },
    ];
  },

  isBuiltIn,
  isDeprecated,
  isHidden,
  isFeatured,
  isPremium,
  isPublic,
  isBeta,
  isOauth,
  isInvite,
  hasUpfrontFields,
  excludePathsApi,
};

export default ServiceUtils;
